import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface GoogleReview {
  id: string;
  author: string;
  rating: number;
  text: string;
  relative_time: string;
  publish_time: string;
  google_uri: string;
  photo_uri?: string;
}

export interface GoogleReviewsPayload {
  place_id: string;
  rating: number | null;
  user_rating_count: number | null;
  google_maps_uri: string | null;
  reviews: GoogleReview[];
  fetched_at: string;
  stale: boolean;
  source: 'live' | 'cache' | 'empty';
}

// Static fallback so the UI is never empty during the very first load
// (before the edge function has warmed up).
const FALLBACK: GoogleReviewsPayload = {
  place_id: 'ChIJjaqzuMfwhmgRlGLLW6bqmck',
  rating: 5.0,
  user_rating_count: 35,
  google_maps_uri: 'https://maps.google.com/?cid=14526900073285771924',
  reviews: [],
  fetched_at: new Date(0).toISOString(),
  stale: true,
  source: 'empty',
};

export function useGoogleReviews() {
  const [data, setData] = useState<GoogleReviewsPayload>(FALLBACK);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: res, error: err } = await supabase.functions.invoke<GoogleReviewsPayload>(
          'google-reviews',
          { body: {} },
        );
        if (cancelled) return;
        if (err) {
          setError(err.message);
        } else if (res) {
          setData(res);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Unknown error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return { data, loading, error };
}
